/**
 * useParty — React hook for party state from Colyseus schema.
 *
 * Reads party membership from room.state.parties (auto-synced by Colyseus)
 * and provides actions to send party messages to the server. Mounted only by
 * the Party window.
 *
 * Invites are not here: the HUD's PartyInvitePrompt owns them, because a hook
 * that lives inside a window only hears an invite while that window is open
 * (stream playtest 2026-10-08, plan 23 row 41).
 *
 * The 500 ms read stays while the window is open: membership changes arrive as
 * messages, but member health/mana/level have no message of their own, and a
 * party message can land before the schema patch that carries the change. The
 * read only re-renders when something the window shows actually changed.
 */

import { useState, useEffect, useCallback } from 'react';
import { ServiceRegistry } from '../../core/ServiceRegistry';

export interface PartyMember {
  sessionId: string;
  name: string;
  level: number;
  job: string;
  health: number;
  maxHealth: number;
  mana: number;
  maxMana: number;
  isLeader: boolean;
}

export interface PartyState {
  partyId: string | null;
  members: PartyMember[];
  isLeader: boolean;
}

interface PartyActions {
  invite: (targetSessionId: string) => void;
  leave: () => void;
  kick: (targetSessionId: string) => void;
}

const NO_PARTY: PartyState = { partyId: null, members: [], isLeader: false };
const POLL_INTERVAL_MS = 500;

/** The party this client's room says it is in (null when it can't tell yet). */
export function readPartyState(room: any): PartyState | null {
  const state = room?.state;
  const sessionId = room?.sessionId;
  if (!state?.parties || !state.players) return null;

  let foundParty: any = null;
  let foundPartyId: string | null = null;
  state.parties.forEach((party: any, id: string) => {
    if (party.memberIds?.includes(sessionId)) {
      foundParty = party;
      foundPartyId = id;
    }
  });
  if (!foundParty) return NO_PARTY;

  const members: PartyMember[] = [];
  for (const memberId of foundParty.memberIds) {
    const player = state.players.get(memberId);
    if (!player) continue;
    members.push({
      sessionId: memberId,
      name: player.heroName || player.name || 'Unknown',
      level: player.level || 1,
      job: player.job || 'beginner',
      health: player.health || 0,
      maxHealth: player.maxHealth || 100,
      mana: player.mana || 0,
      maxMana: player.maxMana || 100,
      isLeader: memberId === foundParty.leaderId,
    });
  }
  return { partyId: foundPartyId, members, isLeader: foundParty.leaderId === sessionId };
}

function samePartyState(a: PartyState, b: PartyState): boolean {
  if (a.partyId !== b.partyId || a.isLeader !== b.isLeader || a.members.length !== b.members.length) return false;
  return a.members.every((m, i) => {
    const n = b.members[i];
    return m.sessionId === n.sessionId && m.name === n.name && m.level === n.level && m.job === n.job
      && m.health === n.health && m.maxHealth === n.maxHealth && m.mana === n.mana
      && m.maxMana === n.maxMana && m.isLeader === n.isLeader;
  });
}

export function useParty(): PartyState & PartyActions {
  const [party, setParty] = useState<PartyState>(NO_PARTY);

  useEffect(() => {
    const read = () => {
      try {
        const registry = ServiceRegistry.getInstance();
        if (!registry.has('NetworkManager')) return;
        const next = readPartyState(registry.get('NetworkManager')?.getRoom?.());
        if (next) setParty(prev => (samePartyState(prev, next) ? prev : next));
      } catch {
        // Non-critical — party UI should never crash the game
      }
    };
    read();
    const interval = setInterval(read, POLL_INTERVAL_MS);
    return () => clearInterval(interval);
  }, []);

  const send = useCallback((type: string, data?: any) => {
    try {
      const registry = ServiceRegistry.getInstance();
      if (!registry.has('NetworkManager')) return;
      const nm = registry.get('NetworkManager');
      const room = nm?.getRoom?.();
      room?.send(type, data || {});
    } catch {
      // Non-critical
    }
  }, []);

  const invite = useCallback((targetSessionId: string) => send('partyInvite', { targetSessionId }), [send]);
  const leave = useCallback(() => send('partyLeave'), [send]);
  const kick = useCallback((targetSessionId: string) => send('partyKick', { targetSessionId }), [send]);

  return { ...party, invite, leave, kick };
}
